import React, { useEffect, useState } from "react";
import "./ParticipantProfileUI.css";
import IconButton from "../IconButton";
import silhouette from "../../images/headshot-silhouette-30.png";

import copyLinkIcon from "../../images/IconButton/copyLink.png";
import facebookLogo from "../../images/IconButton/facebookLogo.png";
import twitterLogo from "../../images/IconButton/xLogo.png";
import emailLogo from "../../images/IconButton/EmailLogo.png";
import ellipses from "../../images/IconButton/Ellipse.png";

import ProfileImage from "../Forms/ParticipantPhotos/ParticipantProfileImage";
import { fetchFeatureFlags } from "../../services/fetchFeatureFlags";
import { FundraiserParticipantShop } from "../../Interfaces/FundraiserRole";
import config from "../../config";
import { usePopup } from "../Popup/PopupContext";
import PopupModel from "../Popup/PopupModel";
import ShareButton from "./ShareStoreComponent";
import {
  DEFAULT_BACKGROUND_COLOR,
  formatPossessive,
  isLightBackground,
} from "../../CommonFunctions";
import { FundraiserResponse } from "../../Interfaces/Fundraiser";

interface LeaderboardEntry {
  id: number;
  name: string;
  image: string;
  amount_raised: number;
}

interface Leaderboard {
  top_sales: LeaderboardEntry[];
  top_profit: LeaderboardEntry[];
  top_non_donation_sales: LeaderboardEntry[];
}
interface LeaderboardProps {
  fundraiser_id: number;
  primary_color: string;
}

const Leaderboard: React.FC<LeaderboardProps> = ({
  fundraiser_id,
  primary_color,
}) => {
  const [entries, setEntries] = useState<Leaderboard | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<
    "top_sales" | "top_profit" | "top_non_donation_sales"
  >("top_sales");

  const primaryIsLightColored = isLightBackground(primary_color);

  const fundraiserLeaderboardDisplayStyles = {
    "--leaderboard-display-bg": primary_color ? primary_color : "#FFFFFF",
    "--leaderboard-display-text-color": primaryIsLightColored
      ? "#000000"
      : "#FFFFFF",
  } as React.CSSProperties;

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${config.API_BASE_URL}fundraiser/${fundraiser_id}/combined-top/?count=5`
        );
        if (!response.ok) {
          throw new Error("Failed to fetch leaderboard data");
        }
        const data: Leaderboard = await response.json();
        setEntries(data);
        setError(false);
      } catch (err) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchLeaderboard();
  }, [fundraiser_id]);

  if (loading) {
    return <p>Loading leaderboard...</p>;
  }

  if (error || !entries) {
    return <p>Failed to load leaderboard. Please try again later.</p>;
  }

  const currentEntries = entries[activeTab];

  return (
    <div
      className="participant-shop-ui-leaderboard"
      style={fundraiserLeaderboardDisplayStyles}
    >
      <h2>Leaderboard</h2>

      {/* Tabs */}
      <div className="participant-shop-ui-tabs">
        <button
          className={`tab-button ${activeTab === "top_sales" ? "active" : ""}`}
          onClick={() => setActiveTab("top_sales")}
        >
          Top Sales
        </button>
        <button
          className={`tab-button ${activeTab === "top_profit" ? "active" : ""}`}
          onClick={() => setActiveTab("top_profit")}
        >
          Top Profit
        </button>
        <button
          className={`tab-button ${
            activeTab === "top_non_donation_sales" ? "active" : ""
          }`}
          onClick={() => setActiveTab("top_non_donation_sales")}
        >
          Top Prize Eligible Sales
        </button>
      </div>

      {/* Leaderboard List */}
      <ul>
        {currentEntries.map((entry, index) => (
          <li key={entry.id} className="participant-shop-ui-leaderboard-item">
            <span className="participant-shop-ui-rank">{index + 1}</span>
            <img
              src={entry.image || silhouette}
              alt={entry.name}
              className="participant-shop-ui-profile-img"
              onError={(e) => (e.currentTarget.src = silhouette)}
            />
            <div className="participant-shop-ui-details">
              <span className="participant-shop-ui-name">{entry.name}</span>
              <div className="participant-shop-ui-amount-container">
                <span className="participant-shop-ui-raised">Raised</span>
                <span className="participant-shop-ui-amount">
                  ${entry.amount_raised.toFixed()}
                </span>
              </div>
            </div>
          </li>
        ))}
      </ul>

      {/* Note */}
      <p className="participant-shop-ui-note">
        *Donations are not prize eligible.
      </p>
    </div>
  );
};

type FundraiserCardProps = {
  image: string;
  name: string;
  amountRaised: number;
  goal: number;
  supporters: number;
  participantObject: FundraiserParticipantShop;
  primary_color: string;
  secondary_color: string;
};

const FundraiserCard: React.FC<FundraiserCardProps> = ({
  image,
  name,
  amountRaised,
  goal,
  supporters,
  participantObject,
  primary_color,
  secondary_color,
}) => {
  const progressPercentage = (amountRaised / goal) * 100;
  const { showPopup, hidePopup } = usePopup();
  const [flags, setFlags] = useState<Record<string, boolean>>({});
  const primaryIsLightColored: boolean = isLightBackground(
    primary_color || DEFAULT_BACKGROUND_COLOR
  );

  const fundraiserCardDisplayStyles = {
    "--card-display-bg": primary_color ? primary_color : "#FFFFFF",
    "--card-display-text-color": primaryIsLightColored ? "#000000" : "#FFFFFF",
  } as React.CSSProperties;

  useEffect(() => {
    const loadFeatureFlags = async () => {
      const flagData = await fetchFeatureFlags();
      setFlags(flagData);
    };

    loadFeatureFlags();
  }, []);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(
      `${config.REACT_APP_SITE_URL}participant-shop/part-id/${participantObject.participant_code}`
    );
    const infoCopiedPopup = new PopupModel(
      "",
      (
        <div style={{ paddingTop: "6%" }}>
          * Participant shop url copied to clipboard! *
        </div>
      ),
      [],
      "30vw"
    );

    showPopup(
      infoCopiedPopup.header,
      infoCopiedPopup.body,
      infoCopiedPopup.buttons,
      infoCopiedPopup.width
    );
  };

  return (
    <div
      className="participant-shop-ui-card"
      style={fundraiserCardDisplayStyles}
    >
      <div className="participant-shop-ui-header">
        <h2>{formatPossessive(name)} Fundraiser</h2>
      </div>
      <div className="participant-shop-ui-content">
        {flags.participant_profile_photo ? (
          <ProfileImage profile_pic_url={participantObject.profile_image_url} />
        ) : (
          // default image
          <img
            src={silhouette}
            alt={name}
            className="participant-shop-ui-participant-image"
          />
        )}
        <div className="participant-shop-ui-info-container">
          <div className="participant-shop-ui-progress">
            <div className="participant-shop-ui-progress-bar">
              <div
                className="participant-shop-ui-progress-filled"
                style={{ width: `${progressPercentage}%` }}
              ></div>
            </div>
          </div>
          <span className="participant-shop-ui-amount-raised">
            ${amountRaised.toFixed()}
          </span>
          <p className="participant-shop-ui-goal-text">
            raised of ${goal.toFixed()} goal
          </p>
          <p className="participant-shop-ui-supporters-count">
            {supporters} supporters
          </p>
          <div className="participant-shop-ui-action-buttons">
            <ShareButton
              participantID={participantObject.participant_code}
              participantName={name}
              organizationName={participantObject.organization_name}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

type ParticipantProfileUIProps = {
  participantShop: FundraiserParticipantShop;
  fundraiser: FundraiserResponse;
};

const ParticipantProfileUI: React.FC<ParticipantProfileUIProps> = ({
  participantShop,
  fundraiser,
}) => {
  const fundraiserUIDisplayStyles = {
    "--profile-display-bg": fundraiser.secondary_color
      ? fundraiser.secondary_color
      : "#FFFFFF",
  } as React.CSSProperties;
  return (
    <div
      className="participant-shop-ui-wrapper"
      style={fundraiserUIDisplayStyles}
    >
      <div className="participant-shop-ui-info">
        <FundraiserCard
          image={`${config.BACKEND_BASE_IMAGE_LOCATION}${participantShop.profile_image_url}`}
          name={participantShop.full_name}
          amountRaised={participantShop.amount_raised}
          goal={participantShop.goal}
          supporters={participantShop.supporter_count}
          participantObject={participantShop}
          primary_color={fundraiser.primary_color || "#FFFFFF"}
          secondary_color={fundraiser.secondary_color || "#FFFFFF"}
        />
      </div>
      <div className="participant-shop-ui-leaderboard">
        <Leaderboard
          fundraiser_id={participantShop.fundraiser}
          primary_color={fundraiser.primary_color || "#FFFFFF"}
        />
      </div>
    </div>
  );
};

export default ParticipantProfileUI;
